import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useQuery } from 'urql';
import { Button as BaseButton } from '~/components/ui/buttons/Button';
import { AssetRuleTemplatesDocument, TemplateBehavior } from '~/gql/graphql';
import { SideLayout } from '~/layouts/side/SideLayout';
import { Asset } from '~/routes/assets';
import { BULK_ASSET_JOB_CREATION_KEY } from '~/constants/bulkAssetOperations';
import { BulkInactivate } from './BulkInactivate';
import {
  canCreateBulkAssetJob,
  filterMatchingQuickCreateTemplates,
  getAssetsWithDeployment,
  getJobCreationDisabledReason,
  getUniqueProperties,
} from './bulkAssetJobEligibility';

type Props = {
  selected: string[];
  selectedAssets: Asset[];
  onClose: () => void;
  reload: () => void;
};

enum BulkAssetAction {
  CREATE_SERVICE = 'CREATE_SERVICE',
  CREATE_CLEAN = 'CREATE_CLEAN',
  CREATE_JOB = 'CREATE_JOB',
  MAKE_INACTIVE = 'MAKE_INACTIVE',
}

const { MAKE_INACTIVE } = BulkAssetAction;

const Button = ({
  children,
  onClick,
  disabled,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}) => {
  return (
    <BaseButton
      as='button'
      className='block w-3/5'
      color='primary'
      onClick={disabled ? undefined : onClick}
      rounded
      disabled={disabled}
    >
      {children}
    </BaseButton>
  );
};

export const BulkAssetEditView = ({
  selected,
  selectedAssets,
  onClose,
  reload,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [action, setAction] = useState<BulkAssetAction | null>(null);

  // Get unique properties from selected assets
  const { properties, assetsWithoutDeployment } = useMemo(
    () => getUniqueProperties(selectedAssets),
    [selectedAssets]
  );

  // Assets with deployments that can be used for job creation
  const assetsWithDeployment = useMemo(
    () => getAssetsWithDeployment(selectedAssets),
    [selectedAssets]
  );

  // Determine if job creation is allowed:
  // - Must have exactly one property
  // - Must have at least one asset with deployment
  const canCreateJob = canCreateBulkAssetJob(
    properties.length,
    assetsWithDeployment.length
  );

  // Query asset rule templates
  // Backend filters by behavior (AssetRules), frontend filters templates by quickCreate enabled and asset types
  const [templateQueryResult] = useQuery({
    query: AssetRuleTemplatesDocument,
    variables: {
      behavior: TemplateBehavior.AssetRules,
    },
    pause: !canCreateJob, // Only fetch when job creation is allowed
    requestPolicy: 'cache-first', // Use cache to avoid unnecessary refetches
  });

  // Extract loading and error states from query result
  const matchingTemplatesLoading = templateQueryResult.fetching;
  const matchingTemplatesError = templateQueryResult.error;

  // Get unique asset types from selected assets with deployments
  const selectedAssetTypes = useMemo(() => {
    return new Set(
      assetsWithDeployment
        .map((asset) => asset.type)
        .filter((type): type is string => Boolean(type))
    );
  }, [assetsWithDeployment]);

  // Filter templates to only those with quickCreate enabled that match selected asset types
  // A template matches if it has no asset type restrictions (empty assetTypes array) OR
  // if at least one of its asset types matches the selected assets' types
  const matchingTemplates: Array<{
    id: string;
    name: string;
    behavior: TemplateBehavior;
  }> = useMemo(() => {
    return filterMatchingQuickCreateTemplates(
      templateQueryResult.data?.jobTemplates,
      selectedAssetTypes
    ).map((template) => ({
      id: template.id,
      name: template.name,
      behavior: template.behavior,
    }));
  }, [templateQueryResult.data, selectedAssetTypes]);

  // Reason why job creation is disabled
  const jobCreationDisabledReason = useMemo(
    () =>
      getJobCreationDisabledReason(
        properties.length,
        assetsWithDeployment.length
      ),
    [properties.length, assetsWithDeployment.length]
  );

  const closeConfirmation = () => setAction(null);

  // Handle create jobs action with different job types
  const handleCreateJob = (jobType: 'service' | 'clean' | 'job') => {
    if (!canCreateJob) return;

    // Store selected assets in session storage for the job form to pick up
    // Include asset type and space info for creating asset tasks
    // Also include job type for naming convention
    // Only include assets with deployments (filtered subset) for consistency
    const assetIdsWithDeployment = assetsWithDeployment.map((a) => a.id);
    sessionStorage.setItem(
      BULK_ASSET_JOB_CREATION_KEY,
      JSON.stringify({
        assetIds: assetIdsWithDeployment,
        jobType, // 'service', 'clean', or 'job'
        assets: assetsWithDeployment.map((a) => ({
          id: a.id,
          name: a.name,
          type: a.type,
          deploymentLocationId: a.deployment?.location?.id,
          deploymentLocationName: a.deployment?.location?.name,
          deploymentSpaceName: a.deployment?.space?.name,
        })),
      })
    );

    // Navigate to bulk job creation form as outlet (keeps assets list visible)
    navigate('/assets/jobs/new', {
      state: {
        fromBulkAssets: true,
        assetIds: assetIdsWithDeployment,
        jobType,
      },
      replace: true, // Replace current history entry to avoid back button issues
    });

    // Don't close - let the outlet handle the display
  };

  // Specific handlers for each job type
  const handleCreateGenericJob = () => handleCreateJob('job');

  // Handle creating job with a specific template
  const handleCreateJobWithTemplate = (templateId: string) => {
    if (!canCreateJob) return;

    // Store selected assets in session storage with template ID
    const assetIdsWithDeployment = assetsWithDeployment.map((a) => a.id);
    sessionStorage.setItem(
      BULK_ASSET_JOB_CREATION_KEY,
      JSON.stringify({
        assetIds: assetIdsWithDeployment,
        jobType: 'job',
        templateId, // Include template ID for the job form to use
        assets: assetsWithDeployment.map((a) => ({
          id: a.id,
          name: a.name,
          type: a.type,
          deploymentLocationId: a.deployment?.location?.id,
          deploymentLocationName: a.deployment?.location?.name,
          deploymentSpaceName: a.deployment?.space?.name,
        })),
      })
    );

    // Navigate to bulk job creation form with template pre-selected
    navigate('/assets/jobs/new', {
      state: {
        fromBulkAssets: true,
        assetIds: assetIdsWithDeployment,
        jobType: 'job',
      },
      replace: true,
    });
  };

  return (
    <div
      className={classNames([
        'fixed inset-0 z-50 h-screen w-full overflow-hidden bg-white lg:relative lg:bg-transparent',
        'lg:w-rsb xl:w-2rsb',
        'lg:h-[calc(100vh-10rem-71px-1px)]',
      ])}
    >
      {action === MAKE_INACTIVE && (
        <BulkInactivate
          selected={selected}
          selectedAssets={selectedAssets}
          onCancel={closeConfirmation}
          onSuccess={() => {
            toast.success(`${selected.length} assets made inactive`);
            reload();
            onClose();
          }}
        />
      )}
      <SideLayout>
        <SideLayout.Head onClose={onClose}>
          {t('selected', { count: selected.length })}
        </SideLayout.Head>
        <SideLayout.Body className='mb-32 flex flex-col items-center justify-center gap-4'>
          {jobCreationDisabledReason && (
            <div className='flex items-center gap-4 rounded-lg border border-orange/60 bg-orange/10 p-3 pl-4 text-orange'>
              <FontAwesomeIcon
                icon={faExclamationTriangle as IconProp}
                className='h-5 w-5 rounded-full border-2  border-orange/30 bg-white/10 p-2'
              />{' '}
              <p className='mt-1 max-w-80'>{jobCreationDisabledReason}</p>
            </div>
          )}
          {/* Info about selected assets */}
          <div className='mb-4 text-center text-sm text-grey-50'>
            {assetsWithoutDeployment.length > 0 && (
              <p className='mt-1 text-xs text-grey-40'>
                {assetsWithoutDeployment.length} asset
                {assetsWithoutDeployment.length !== 1 ? 's' : ''} without
                deployment
              </p>
            )}
          </div>

          {/* Actions */}
          <div className='flex w-full max-w-xs flex-col items-center justify-center gap-3'>
            <Button onClick={handleCreateGenericJob} disabled={!canCreateJob}>
              {t('Create Job')}
            </Button>

            {/* Display matching template buttons */}
            {canCreateJob && (
              <div className='flex w-full flex-col items-center space-y-2 border-b border-t border-outline-variant py-3'>
                <p className='mb-0.5 text-center text-xs font-medium text-secondary'>
                  {t('bulkEdit.quickCreateTitle')}
                </p>
                {matchingTemplatesLoading ? (
                  <p className='text-center text-xs text-secondary'>
                    {t('bulkEdit.loadingTemplates')}
                  </p>
                ) : matchingTemplatesError ? (
                  <p className='text-center text-xs text-orange'>
                    {t('bulkEdit.templatesError')}
                  </p>
                ) : matchingTemplates.length > 0 ? (
                  matchingTemplates.map((template) => (
                    <Button
                      key={template.id}
                      onClick={() => handleCreateJobWithTemplate(template.id)}
                    >
                      {t('bulkEdit.createTemplate', {
                        templateName: template.name,
                      })}
                    </Button>
                  ))
                ) : (
                  <p className='text-center text-xs text-secondary'>
                    {t('bulkEdit.noMatchingTemplates')}
                  </p>
                )}
              </div>
            )}

            <Button onClick={() => setAction(MAKE_INACTIVE)}>
              {t('Make Inactive')}
            </Button>
          </div>
        </SideLayout.Body>
      </SideLayout>
    </div>
  );
};
